import React, { useEffect, useState } from 'react';
import DirectionService from '../../../services/DirectionService';
import MissionTransportService from '../../../services/MissionTransportService';
import { Dialog } from '@material-ui/core';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  TextField,
} from '@mui/material';
import Spacer from '../../../components/Spacer';
import SendIcon from '@mui/icons-material/Send';
import useStyles from '../../../styles/MissionAssignVehicle.style';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import GroupIcon from '@mui/icons-material/Group';
import AssignmentIcon from '@mui/icons-material/Assignment';

const bullRed = (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      mx: '5px',
      transform: 'scale(0.8)',
      color: 'red',
      fontSize: '20px',
    }}
  >
    *
  </Box>
);

const toPositiveInt = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const buildInitialFormRequest = ({ missionId = 0, agendaId = 0, selectedDestinations = [] } = {}) => {
  const destinationIds = [];
  const missionnaryMap = new Map();

  (selectedDestinations || []).forEach((destination) => {
    const destinationId = toPositiveInt(
      destination?.mission_timesheet_destination_id ?? destination?.mission_destination_id
    );
    if (destinationId) {
      destinationIds.push(destinationId);
    }

    (destination?.missionnaries || []).forEach((missionnary) => {
      const detailId = toPositiveInt(missionnary?.mission_planning_agenda_details_id);
      const userId = toPositiveInt(missionnary?.user_id ?? missionnary?.missionary_id);
      const mapKey = detailId ? `detail-${detailId}` : userId ? `user-${userId}` : null;
      if (!mapKey) {
        return;
      }
      if (!missionnaryMap.has(mapKey)) {
        missionnaryMap.set(mapKey, {
          ...missionnary,
          mission_planning_agenda_details_id: detailId,
          user_id: userId,
          nom: missionnary?.nom || missionnary?.name || '',
        });
      }
    });
  });

  const allMissionnaries = Array.from(missionnaryMap.values());

  return {
    show: false,
    direction: null,
    description: '',
    missionnaries: [],
    allMissionnaries,
    destination: destinationIds,
    agendaId: agendaId || 0,
    missionId: missionId || 0,
    comment: '',
  };
};

const MissionVehicleSolicitation = (props) => {
  const {
    missionId,
    loading,
    open,
    onClose,
    setMessage,
    agendaId,
    selectedDestinations = [],
  } = props;

  const classes = useStyles();
  const [directions, setDirections] = useState([]);
  const [formRequest, setFormRequest] = useState(
    buildInitialFormRequest({ missionId, agendaId, selectedDestinations })
  );

  useEffect(() => {
    async function fetchDirections() {
      try {
        const result = await DirectionService.getDirection();
        if (result?.data) {
          setDirections(result.data);
        }
      } catch (error) {
        console.error('Error fetching directions:', error);
      }
    }

    fetchDirections();
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFormRequest(buildInitialFormRequest({ missionId, agendaId, selectedDestinations }));
  }, [open, missionId, agendaId, selectedDestinations, loading]);

  const resetAndClose = () => {
    setFormRequest(buildInitialFormRequest({ missionId, agendaId, selectedDestinations }));
    onClose();
  };

  const handleChangeRequest = (_, name, value) => {
    setFormRequest((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRequestDirection = async () => {
    const selectedMissionnaries = Array.isArray(formRequest?.missionnaries) ? formRequest.missionnaries : [];
    const missionPlanningAgendaDetails = [
      ...new Set(
        selectedMissionnaries
          .map((missionnary) => missionnary?.mission_planning_agenda_details_id)
          .filter(Boolean)
      ),
    ];

    if (!formRequest.direction) {
      setMessage({ open: true, color: 'red', messageInfo: 'Veuillez selectionner une direction.' });
      return;
    }

    if (!selectedMissionnaries.length) {
      setMessage({ open: true, color: 'red', messageInfo: 'Veuillez selectionner au moins un missionnaire.' });
      return;
    }

    if (!missionPlanningAgendaDetails.length) {
      setMessage({
        open: true,
        color: 'red',
        messageInfo: 'Aucune ligne mission_planning_agenda_details valide n a ete trouvee.',
      });
      return;
    }

    const dataToSend = {
      mission_planning_id: formRequest.missionId,
      direction_id: formRequest.direction,
      comment: formRequest.comment,
      mission_planning_agenda_details: missionPlanningAgendaDetails,
    };

    try {
      const result = await MissionTransportService.sendVehicleRequestDirection(dataToSend);
      if (result?.status) {
        setMessage({ open: true, color: 'green', messageInfo: 'La demande a ete envoyee avec succes.' });
        resetAndClose();
        return;
      }
      setMessage({
        open: true,
        color: 'red',
        messageInfo: result?.message || 'Erreur lors de l envoi de la demande.',
      });
    } catch (error) {
      setMessage({ open: true, color: 'red', messageInfo: 'Erreur lors de l envoi de la demande.' });
    }
  };

  const destinationDateOptions = (selectedDestinations || [])
    .map((destination) => ({
      mission_destination_id: destination?.mission_destination_id ?? null,
      mission_timesheet_destination_id: destination?.mission_timesheet_destination_id ?? null,
      date_begin: destination?.date_begin || null,
      date_end: destination?.date_end || null,
    }))
    .filter((option) => option.date_begin || option.date_end);

  const formatDateRange = (option) => {
    const dateBegin = option?.date_begin
      ? new Date(option.date_begin).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        })
      : '';
    const dateEnd = option?.date_end
      ? new Date(option.date_end).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        })
      : '';
    return dateBegin && dateEnd ? `${dateBegin} - ${dateEnd}` : dateBegin || dateEnd || '';
  };

  const selectedDateOptions = destinationDateOptions.filter((option) =>
    formRequest.destination.some(
      (id) =>
        Number(id) === Number(option?.mission_timesheet_destination_id) ||
        Number(id) === Number(option?.mission_destination_id)
    )
  );

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaperCompact }}
      onClose={resetAndClose}
    >
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        {'Sollicitation de voiture d\'une autre direction'}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <DialogContentText id="alert-dialog-description" className={`${classes.dialogText} ${classes.formArea}`}>
          <Alert severity="info" sx={{ borderRadius: '10px' }}>
            <i>
              Le responsable de la direction selectionnee sera charge d assigner une voiture pour les lignes
              preselectionnees dans la timesheet.
            </i>
          </Alert>

          <Spacer y={1} />
          <fieldset className={classes.fieldset}>
            <legend>Informations de sollicitation</legend>

          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 1 }}>
            <label className={classes.labelPosition}>
              <EventIcon fontSize="small" />
              Dates des destinations concernees {bullRed}
            </label>
           <Autocomplete  
              multiple
              id="dates-destinations"
              options={destinationDateOptions}
              getOptionLabel={formatDateRange}
              isOptionEqualToValue={(option, value) =>
                (option?.mission_timesheet_destination_id ?? null) ===
                  (value?.mission_timesheet_destination_id ?? null) &&
                (option?.mission_destination_id ?? null) === (value?.mission_destination_id ?? null)
              }
              disableCloseOnSelect
              onChange={(event, value) => {
                const destinationIds = (value || [])
                  .map((item) =>
                    toPositiveInt(item?.mission_timesheet_destination_id ?? item?.mission_destination_id)
                  )
                  .filter(Boolean);
                handleChangeRequest(event, 'destination', destinationIds);
              }}
              value={selectedDateOptions}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Dates des destinations" variant="outlined" size="small" />
              )}
            />
          </FormControl>

          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 2 }}>
            <label className={classes.labelPosition}>
              <PlaceIcon fontSize="small" />
              Direction de la demande {bullRed}
            </label>
            <Autocomplete
              id="direction-request"
              options={directions}
              getOptionLabel={(option) => (option ? `${option.name} (${option.code})` : '')}
              filterSelectedOptions
              onChange={(event, value) => handleChangeRequest(event, 'direction', value?.direction_id ?? null)}
              value={directions.find((direction) => direction.direction_id === formRequest.direction) || null}
              fullWidth
              renderInput={(params) => <TextField {...params} label="Direction" variant="outlined" size="small" />}
            />
          </FormControl>

          <Spacer y={1} />
          <FormControl fullWidth variant="outlined" size="small">
            <label className={classes.labelPosition}>
              <GroupIcon fontSize="small" />
              Missionnaires {bullRed}
            </label>
            <Autocomplete
              multiple
              id="missionnaries-request"
              options={formRequest.allMissionnaries || []}
              getOptionLabel={(option) => (option ? option.nom || option.name || '' : '')}
              isOptionEqualToValue={(option, value) =>
                (option?.mission_planning_agenda_details_id ?? null) ===
                  (value?.mission_planning_agenda_details_id ?? null) &&
                (option?.user_id ?? null) === (value?.user_id ?? null)
              }
              filterSelectedOptions
              onChange={(event, value) => handleChangeRequest(event, 'missionnaries', value || [])}
              value={formRequest.missionnaries || []}
              fullWidth
              renderInput={(params) => (
                <TextField {...params} label="Missionnaires" variant="outlined" size="small" />
              )}
            />
          </FormControl>

          <FormControl fullWidth variant="outlined" size="small" sx={{ mt: 1 }}>
            <label className={classes.labelPosition}>
              <AssignmentIcon fontSize="small" />
              Commentaire
            </label>
            <TextField
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              value={formRequest.comment}
              onChange={(event) => handleChangeRequest(event, 'comment', event.target.value)}
              InputProps={{
                sx: {
                  overflow: 'auto',
                },
              }}
            />
          </FormControl>
          </fieldset>
        </DialogContentText>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button onClick={resetAndClose} variant="outlined" className={classes.actionButton}>
          Annuler
        </Button>
        <Button
          startIcon={<SendIcon />}
          variant="contained"
          onClick={handleRequestDirection}
          color="primary"
          autoFocus
          className={classes.actionButton}
        >
          Envoyer la demande
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissionVehicleSolicitation;
